import { classnames } from 'Utils/classnames/classnames.utils';
import { useRouter } from 'next/router';
import type { ReactNode } from 'react';
import React from 'react';
import URI from 'urijs';
import { url as urlUtils } from '@clubmed/utils';
import { ICONS } from 'Components/utils/icon/icons';
import { useAppContext } from 'Context/ReactAppContext';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { QUERY_DEPARTURE_DATE, QUERY_DURATION, QUERY_TRANSPORT } from 'Utils/uri/constants';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink';
import { Icon } from 'Components/frameworks/icon/Icon';
import { ExclusiveCollectionBadge } from 'Components/widgets/exclusive-collection-badge/ExclusiveCollectionBadge';
import type { MergedThemeStyles, ThemeKey } from 'Src/types/widget-helpers';
import { themes } from './ProductBlockHeader.themes';
type Props = {
  className?: string;
  onClick?: () => unknown;
  hasBackIcon?: boolean;
  product: Product;
  queryParamsToIgnore?: ReadonlyArray<string>;
  theme?: ThemeKey<typeof themes>;
};

const buildUrl = (availableLocales, locale, product, queryParams) => {
  if (!product?.href) {
    return undefined;
  }

  const url = new URI(product.href);

  if (availableLocales && Object.keys(availableLocales).length > 1 && locale) {
    // we only keep locale in url if we have at least two availables locales
    url.addQuery({
      locale,
    });
  }

  const departureDateQueryParam = queryParams[QUERY_DEPARTURE_DATE];

  if (departureDateQueryParam) {
    url.addQuery({
      [QUERY_DEPARTURE_DATE]: departureDateQueryParam,
    });
  }

  const departureDurationQueryParam = queryParams[QUERY_DURATION];

  if (departureDurationQueryParam) {
    url.addQuery({
      [QUERY_DURATION]: departureDurationQueryParam,
    });
  }

  const departureCityQueryParam = queryParams[QUERY_TRANSPORT];

  if (departureCityQueryParam) {
    url.addQuery({
      [QUERY_TRANSPORT]: departureCityQueryParam,
    });
  }

  return url.toString() ? urlUtils.normalizeUrl(url.toString()) : undefined;
};

export const ProductBlockHeader = ({
  className,
  onClick,
  product,
  hasBackIcon,
  queryParamsToIgnore,
  theme = 'teaser',
}: Props) => {
  const appContext = useAppContext();
  const { locale, availableLocales } = appContext;
  const isGsa = isFeatureFlipped(appContext)('isGsa');
  const { query } = useRouter();
  const { thBack, thBadge, thCountry, thECBadge, thInner, thName, thRoot } = themes[
    theme
  ] as MergedThemeStyles<typeof themes>;
  const wrapperClassName = classnames(thRoot, className);
  const wrapperProps = isGsa
    ? {}
    : {
        className: wrapperClassName,
        'data-testid': 'ProductBlockHeader-link',
        onClick,
        queryParamsToIgnore: queryParamsToIgnore,
        tagname: 'div',
        url: buildUrl(availableLocales, locale, product, query),
      };
  const Wrapper = isGsa
    ? ({ children }: { children: ReactNode }) => <div className={wrapperClassName}>{children}</div> // for Gsa locales we don't want to be ProductBlockHeader to be a link but the content should still be displayed
    : DynamicLink;
  return (
    <Wrapper {...wrapperProps}>
      {hasBackIcon && <Icon className={thBack} name={ICONS.ARROW_LEFT} />}
      <span className={thInner}>
        <span className={thName} data-testid="ProductBlockHeader-name">
          <span>{product.name}</span>
          {product.isExplicitEC && <ExclusiveCollectionBadge className={thECBadge} />}
          {product.seasonTrad && <span className={thBadge}>{product.seasonTrad}</span>}
        </span>
        <span className={thCountry} data-testid="ProductBlockHeader-country">
          {product?.destination?.country}
        </span>
      </span>
    </Wrapper>
  );
};
